'use client';

import { FeatherBellOff, FeatherCheck, FeatherX } from '@subframe/core';
import { useState } from 'react';
import { useAppDispatch } from '@/store/hooks';
import { createNotificationMute } from '@/features/notification-preferences/store';
import { CATEGORY_LABEL } from '@/shared/constants/notificationCategories';
import { useToast } from '@/shared/providers';
import { dismissNotification, itemRemoved, markNotificationAsRead } from '../store';
import type { FonteNotificacoes, NotificacaoItem } from '@/types/notificationBell/types';
interface Props {
  item: NotificacaoItem;
  fonte: FonteNotificacoes;
  scoped?: boolean;
  onActivate?: () => void;
}
export function NotificationItem({
  item,
  fonte,
  scoped = false,
  onActivate
}: Props) {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const [muting, setMuting] = useState(false);
  // O mute do core (`/notifications/mute`) exige workspace — a equipe interna levaria 403.
  const podeSilenciar = !scoped && fonte === 'workspace';
  const handleMarkAsRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (item.lida) return;
    dispatch(markNotificationAsRead({
      id: item.id,
      fonte
    }));
  };
  const handleDismiss = (e_0: React.MouseEvent) => {
    e_0.stopPropagation();
    dispatch(dismissNotification({
      id: item.id,
      fonte
    }));
  };
  const handleMuteEntity = async (e_1: React.MouseEvent) => {
    e_1.stopPropagation();
    if (muting) return;
    setMuting(true);
    try {
      await dispatch(createNotificationMute({
        scope: 'ENTITY',
        entityType: item.entityType,
        entityId: item.entityId
      })).unwrap();
      dispatch(itemRemoved(item.id));
      toast.showSuccess('Item silenciado', 'Você pode reativar em Preferências → Notificações.');
    } catch {
      toast.showError('Não foi possível silenciar');
    } finally {
      setMuting(false);
    }
  };
  return <div role={onActivate ? 'button' : undefined} tabIndex={onActivate ? 0 : undefined} onClick={onActivate} onKeyDown={onActivate ? event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onActivate();
    }
  } : undefined} className={`relative flex gap-[10px] border-b border-neutral-100 px-4 py-3 transition-colors last:border-b-0 ${!item.lida ? 'bg-brand-50 hover:bg-brand-50/80' : 'hover:bg-neutral-50'} ${onActivate ? 'cursor-pointer' : ''}`}>
      <div className={`mt-[6px] h-2 w-2 shrink-0 rounded-full transition-colors ${!item.lida ? 'bg-brand-400' : 'bg-transparent'}`} />

      <div className="min-w-0 flex-1">
        <div className="mb-[3px] flex flex-wrap items-center gap-[6px]">
          <span className="rounded bg-neutral-100 px-[5px] py-[1px] text-[11px] font-semibold text-neutral-500">
            {scoped ? 'Pendência' : CATEGORY_LABEL[item.categoria]}
          </span>
        </div>
        <div className="text-default-font mb-[4px] text-[13.5px] font-semibold">{item.titulo}</div>
        <div className="text-subtext-color text-[12.5px] leading-[1.5]">
          {item.prazo > 0 ? `Vence em ${item.prazo}d` : `Atrasado há ${Math.abs(item.prazo)}d`}
        </div>

        <div className="mt-2 flex items-center gap-[6px]">
          {!item.lida && <button type="button" onClick={handleMarkAsRead} className="text-subtext-color hover:text-default-font inline-flex items-center gap-1 rounded border border-neutral-200 px-[10px] py-[5px] text-[12px] font-medium transition-colors hover:border-neutral-300">
              <FeatherCheck className="h-[14px] w-[14px]" />
              Marcar como lida
            </button>}
          {podeSilenciar ? <button type="button" onClick={handleMuteEntity} disabled={muting} className="text-subtext-color hover:text-default-font inline-flex items-center gap-1 rounded border border-neutral-200 px-[10px] py-[5px] text-[12px] font-medium transition-colors hover:border-neutral-300 disabled:opacity-50">
              <FeatherBellOff className="h-[14px] w-[14px]" />
              Silenciar este item
            </button> : null}
        </div>
      </div>

      <button type="button" onClick={handleDismiss} className="absolute top-[10px] right-[10px] rounded p-[2px] text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600" aria-label="Dispensar">
        <FeatherX className="h-[13px] w-[13px]" />
      </button>
    </div>;
}